import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * SlideDown component that animates the opening and closing of content
 *
 * @param {Object} props - Component props
 * @param {boolean} props.closed - Whether the component is closed (not showing content)
 * @param {string} props.className - Additional class name for styling
 * @param {ReactNode} props.children - Content to be displayed when open
 * @returns {JSX.Element} - The SlideDown component
 */
const SlideDown = ({ closed = false, className = "", children }) => {
    const contentRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const content = contentRef.current;
        const wrapper = wrapperRef.current;

        if (content && wrapper) {
            if (closed) {
                wrapper.style.height = "0px";
                wrapper.style.overflow = "hidden";
            } else {
                wrapper.style.height = `${content.offsetHeight}px`;
                wrapper.style.overflow = "hidden";

                // Add a timeout to remove the height constraint after animation
                const timeout = setTimeout(() => {
                    if (!closed && wrapper) {
                        wrapper.style.height = "";
                        wrapper.style.overflow = "";
                    }
                }, 300);

                return () => clearTimeout(timeout);
            }
        }
    }, [closed]);

    return (
        <div
            ref={wrapperRef}
            className={className}
            style={{
                transition: "height 0.3s ease",
                height: closed ? "0px" : "",
                overflow: closed ? "hidden" : "",
            }}
        >
            <div ref={contentRef}>{children}</div>
        </div>
    );
};

SlideDown.propTypes = {
    closed: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};

export default SlideDown;
